
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Student } from '@/types/models';
import { useFamiliesData } from '@/hooks/useFamiliesData';
import { FormDatePicker } from '@/components/ui/form-date-picker';
import { FormBirthdayDatePicker } from '@/components/ui/form-birthday-date-picker';
import { Form } from '@/components/ui/form';
import { Users, UserPlus, Calendar } from 'lucide-react';

const formSchema = z.object({
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().min(1, 'Last name is required'),
  preferredName: z.string().optional(),
  dateOfBirth: z.string().optional(),
  gender: z.string().optional(),
  notes: z.string().optional(),
  familyId: z.string().optional(),
});

export type EnhancedStudentFormValues = z.infer<typeof formSchema>;

interface AddStudentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onStudentAdded: (student: any) => Promise<void>;
  isLoading?: boolean;
}

const AddStudentDialog: React.FC<AddStudentDialogProps> = ({
  open,
  onOpenChange,
  onStudentAdded,
  isLoading = false,
}) => {
  const { families } = useFamiliesData();
  const [showCreateFamily, setShowCreateFamily] = useState(false);

  const form = useForm<EnhancedStudentFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      preferredName: '',
      dateOfBirth: '',
      gender: '',
      notes: '',
      familyId: '',
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = form;

  const selectedFamilyId = watch('familyId');

  const handleFormSubmit = async (data: EnhancedStudentFormValues) => {
    try {
      const studentData = {
        first_name: data.firstName,
        last_name: data.lastName,
        preferred_name: data.preferredName || null,
        date_of_birth: data.dateOfBirth || null,
        gender: data.gender || null,
        notes: data.notes || '',
        family_id: data.familyId === 'unassigned' ? null : data.familyId || null,
      };
      
      await onStudentAdded(studentData);
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error in AddStudentDialog:', error);
    }
  };

  const familyOptions = families.map((family) => ({
    id: family.id,
    name: family.name,
  }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Add New Student
          </DialogTitle>
          <DialogDescription>
            Create a new student profile and optionally assign them to a family
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
          {/* Student Information */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Student Information
            </h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name *</Label>
                <Input
                  id="firstName"
                  {...register('firstName')}
                  placeholder="Enter first name"
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name *</Label>
                <Input
                  id="lastName"
                  {...register('lastName')}
                  placeholder="Enter last name"
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName.message}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferredName">Preferred Name (Optional)</Label>
              <Input
                id="preferredName"
                {...register('preferredName')}
                placeholder="Nickname or preferred name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormBirthdayDatePicker
                name="dateOfBirth"
                label="Date of Birth"
                placeholder="Select date of birth"
              />

              <div className="space-y-2">
                <Label htmlFor="gender">Gender (Optional)</Label>
                <Select onValueChange={(value) => setValue('gender', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                    <SelectItem value="prefer-not-to-say">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea
                id="notes"
                {...register('notes')}
                placeholder="Any additional notes about the student"
                rows={3}
              />
            </div>
          </div>

          <Separator />

          {/* Family Assignment */}
          <div className="space-y-4">
            <h3 className="font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Family Assignment
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="familyId">Assign to Family (Optional)</Label>
              <Select onValueChange={(value) => setValue('familyId', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a family or leave unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Leave unassigned</SelectItem>
                  {familyOptions.map((family) => (
                    <SelectItem key={family.id} value={family.id}>
                      {family.name} Family
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {selectedFamilyId && selectedFamilyId !== 'unassigned' && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-sm text-green-700">
                  ✓ Student will be assigned to the {families.find(f => f.id === selectedFamilyId)?.name} family
                </p>
              </div>
            )}

            {(!selectedFamilyId || selectedFamilyId === 'unassigned') && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  💡 You can assign this student to a family later from their profile page
                </p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset();
                onOpenChange(false);
              }}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Adding Student...' : 'Add Student'}
            </Button>
          </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default AddStudentDialog;
